import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ButtonModule } from 'primeng/button';

import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { TableTemplateComponent } from 'src/app/templates/table-template/table-template.component';
import { UserAutocompleteComponent } from "src/app/templates/user-autocomplete/user-autocomplete.component";
import { ActionButtonEvent, ColumnType, InputType, TableColumnModel } from 'src/app/templates/table-template/tableModel';
import { BaseTableComponent } from 'src/app/layout/base-table-component';
import { AdminRole } from 'src/app/models/constants';
import { UserModel } from 'src/app/models/userModel';

@Component({
  selector: 'app-user-administration',
  imports: [TableTemplateComponent, MatInputModule, MatSelectModule, MatButtonModule,
    ButtonModule, MatIconModule, MatProgressSpinnerModule, UserAutocompleteComponent],
  templateUrl: './user-administration.component.html',
  styleUrl: './user-administration.component.scss'
})
export class UserAdministrationComponent extends BaseTableComponent {
  @ViewChild(TableTemplateComponent) tableComponent!: TableTemplateComponent;
  @ViewChild('autoCompleteUserTemplate', { static: true }) autoCompleteUserTemplateRef!: TemplateRef<any>;
  @Input() readonly currentAdminPage!: AdminRole;
  readonly title: string;
  readonly currentUser: UserModel;
  readonly emailIdPlaceholder: string;
  canModifyAdmin = false;
  //Flag to show Submit button below table for multiple changes at once
  isBulkSave = false;
  //Array to hold items to be added/updated/deleted if BulkSave is true
  itemsToSave: any[] = [];
  loader = { isLoading: true, message: 'Loading Admin Configuration...' };
  errorMessage = '';
  plantColumn!: TableColumnModel;
  shopColumn!: TableColumnModel;

  constructor(router: Router, route: ActivatedRoute, private readonly mfgappsApiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService) {
    super(router, route);
    //Placeholder with email domain info until we complete Toyota Graph API integration
    this.emailIdPlaceholder = `Enter Email ID ending with ${this.mfgappsApiService.userEmailDomain}`;
    this.currentUser = this.eventService.getActiveAccount();
    //The admin role will be passed as route data configured in app.routes.ts
    this.currentAdminPage = this.route.snapshot.data?.['currentAdminPage'];
    this.title = `${this.currentAdminPage} Configuration`;
    //Allow super admins to edit all, company admins can edit shop admins, shop admins cannot edit
    this.canModifyAdmin = this.currentUser?.isSuperAdmin ||
      (this.currentUser?.adminCompanyId > 0 && this.currentAdminPage === AdminRole.SHOP_ADMIN);
    Object.defineProperty(this, 'canModifyAdmin', { writable: false });
  }

  async ngOnInit() {
    //Common column definitions
    this.columnList = [
      { name: "company_id", label: "Company Name", type: ColumnType.category, matchMode: 'in', 
        required: true, displayField: "company_code", filterField: "company_code", 
        inputType: InputType.select, inputPlaceholder: 'Select Company' },
      { name: "email_id", label: "User Email ID", type: ColumnType.category, matchMode: 'in', colSize: 'long',
        //inputType: InputType.text, inputPlaceholder: this.emailIdPlaceholder,
        required: true, inputType: InputType.template, editTemplate: this.autoCompleteUserTemplateRef }
    ];
    this.multiSortMeta = [
      { field: 'company_code', order: 1 },
      { field: 'email_id', order: 1 }
    ];
    if (this.currentAdminPage === AdminRole.COMPANY_ADMIN) {
      this.initializeCompanyAdmin();
    }
    else if (this.currentAdminPage === AdminRole.SHOP_ADMIN) {
      this.initializeShopAdmin();
    }
    //Set dropdown options for company column (1st column) in table with companies the user has access to
    this.columnList[0].inputSelectOptions = await this.eventService.getUserCompanyList();
    await this.loadAdminConfiguration();
  }

  initializeCompanyAdmin() {
    //Future enhancement: If needed to add more columns specific to Company Admin, can be added here
  }

  initializeShopAdmin() {
    this.plantColumn = { name: "plant_id", label: "Plant Name", type: ColumnType.category, matchMode: 'in',
      required: true, displayField: "plant_name", filterField: "plant_name", inputType: InputType.select,
      inputPlaceholder: 'Select Plant' };
    this.shopColumn = { name: "shop_id", label: "Shop Name", type: ColumnType.category, matchMode: 'in',
      required: true, displayField: "shop_name", filterField: "shop_name", inputType: InputType.select, 
      inputPlaceholder: 'Select Shop' };
    //Insert plant and shop columns after company column (index 0) in column list and multi sort meta
    this.columnList.splice(1, 0, this.plantColumn, this.shopColumn);
    this.multiSortMeta.splice(1, 0, ...[
      { field: 'plant_name', order: 1 },
      { field: 'shop_name', order: 1 }
    ]);
  }

  //Return role name in lowercase without spaces ('companyadmin'/'shopadmin') as query param in API calls
  get currentRole() {
    return this.currentAdminPage.replace(' ', '').toLowerCase();
  }

  async loadAdminConfiguration() {
    try {
      this.tableData = [];
      this.itemsToSave = [];
      this.loader.isLoading = true;
      this.loader.message = `Loading ${this.title}...`;
      this.errorMessage = '';
      const response = await this.mfgappsApiService.getAdmin(this.currentRole);
      if (Array.isArray(response['result'])) {
        this.tableData = response['result'];
      }
      //update URL query params on data load
      this.manageRouteParameters();
      this.resetLoader();
    }
    catch (error) {
      this.handleApiError(error, this.loader.message, true);
    }
  }

  async onCancel() {
    if (await this.eventService.confirmDialog('Any unsaved configuration changes will be lost. Do you wish to proceed?')) {
      await this.loadAdminConfiguration();
    }
  }

  async onSubmit() {
    if (this.tableComponent?.isEditMode) {
      this.eventService.showSnackBar('An Admin item is being edited, please exit the edit mode !!', 'warn');
      return;
    }
    if (await this.eventService.confirmDialog(`Do you want to save all ${this.title} changes?`)) {
      this.loader.message = `Updating ${this.title}...`;
      try {
        await this.mfgappsApiService.updateAdmin(this.itemsToSave);
        this.eventService.showSnackBar(`Updated ${this.title} successfully !`, 'success');
        await this.loadAdminConfiguration();
      }
      catch (error) {
        this.handleApiError(error, this.loader.message);
      }
    }
  }

  handleApiError(error: any, action: string, hideTable = false) {
    console.info(error);
    this.resetLoader();
    const isClientError = error.statusCode >= 400 && error.statusCode < 500;
    //Show appropriate start of message based on 4xx (client) or 5xx (server) errors
    let errorMessage = `${isClientError ? 'Invalid Operation' : 'Error Occurred'} while ${action}: ${error.message}`;
    if (!isClientError) {
      //Show message to try again only for 4xx (client) errors like unauthorized/forbidden
      errorMessage = errorMessage.concat(", Please try again !");
    }
    if (hideTable) {
      this.errorMessage = errorMessage;
    }
    this.eventService.showSnackBar(errorMessage, 'error');
  }

  validateItem(adminItem: any) {
    adminItem.email_id = adminItem.email_id?.trim().toLowerCase();
    if (!adminItem.email_id?.endsWith(this.mfgappsApiService.userEmailDomain)) {
      return { isValid: false, message: this.emailIdPlaceholder };
    }
    return { isValid: true, message: '' };
  }

  async handleButtonClicked(event: ActionButtonEvent) {
    const adminItem = event.entry;
    if (event.action === 'init') {
      // If existing Shop Admin item, set plant and shop dropdown options based on plant_id and shop_id
      if (this.currentAdminPage === AdminRole.SHOP_ADMIN && adminItem.id !== 'new') {
        this.plantColumn.inputSelectOptions = Object.values(this.eventService.userPlantMap)
          .filter(plant => plant.company_id === adminItem.company_id);
        this.shopColumn.inputSelectOptions = Object.values(this.eventService.userShopMap)
          .filter(shop => shop.plant_id === adminItem.plant_id);
      }
    }
    else if (event.action === 'cancel' && adminItem.id === 'new') {
      //If edit is canceled on adding new item, remove the new item from table data
      this.tableComponent.deleteEntry(adminItem);
    }
    else if (event.action === 'delete' && !this.tableComponent.isEditMode) {
      if (await this.eventService.confirmDialog(`Do you want to delete ${this.currentAdminPage} '${adminItem.email_id}'?`)) {
        await this.handleDeleteAdmin(adminItem);
      }
    }
  }

  async handleAddUpdateAdmin(adminItem: any) {
    let canExitEditMode = true;
    //Set company_code, plant_name and shop_name for display in table after saving the row
    adminItem.company_code = this.eventService.userCompanyMap[adminItem.company_id]?.company_code;
    if (this.currentAdminPage === AdminRole.SHOP_ADMIN) {
      adminItem.plant_name = this.eventService.userPlantMap[adminItem.plant_id]?.plant_name;
      adminItem.shop_name = this.eventService.userShopMap[adminItem.shop_id]?.shop_name;
    }
    if (this.isBulkSave) {
      adminItem.action = adminItem.id === 'new' ? 'add' : 'update';
      this.itemsToSave.push(adminItem);
    }
    else {
      const actionPrefix = adminItem.id === 'new' ? 'Adding' : 'Updating';
      const action = `${actionPrefix} ${this.currentAdminPage}`;
      this.loader.message = `${action}...`;
      try {
        const completedAction = adminItem.id === 'new' ? 'Added' : 'Updated';
        const response = adminItem.id === 'new' ? await this.mfgappsApiService.postAdmin(adminItem) :
          await this.mfgappsApiService.patchAdmin(adminItem.id, adminItem);
        //Update id for newly added admin item from API response
        if (adminItem.id === 'new') {
          adminItem.id = response.result.id;
        }
        this.eventService.showSnackBar(`${completedAction} ${this.currentAdminPage} '${adminItem.email_id}' successfully !`, 'success');
      }
      catch (error) {
        //Return flag as false to retain row edit mode in table component on API error
        canExitEditMode = false;
        this.handleApiError(error, action);
      }
      this.resetLoader();
    }
    return canExitEditMode;
  }

  async handleDeleteAdmin(adminItem: any) {
    //Usually new entry has to be saved or canceled before deleting, but just in case handle it here
    if (adminItem.id === 'new') {
      this.tableComponent.deleteEntry(adminItem);
      return;
    }
    if (this.isBulkSave) {
      adminItem.action = 'delete';
      this.itemsToSave.push(adminItem);
    }
    else {
      const action = `Deleting ${this.currentAdminPage}`;
      this.loader.message = `${action}...`;
      try {
        await this.mfgappsApiService.deleteAdmin(adminItem.id);
        this.tableComponent.deleteEntry(adminItem);
        this.eventService.showSnackBar(`Deleted ${this.currentAdminPage} '${adminItem.email_id}' successfully !`, 'success');
      }
      catch (error) {
        this.handleApiError(error, action);
      }
      this.resetLoader();
    }
  }

  addNewAdminItem() {
    if (this.tableComponent && !this.tableComponent.isEditMode) {
      //Add a new admin item with id 'new' with company_id, plant_id and shop_id as null
      const newAdmin = {
        id: 'new', email_id: '', role_id: this.currentRole,
        corporate_id: this.eventService.corporateId, company_id: null, plant_id: null, shop_id: null
      };
      this.tableData.push(newAdmin);
      this.tableComponent.initRowEdit(newAdmin, 'first');
    }
  }

  onEditInputChanged(eventDetails: { event: any, entry: any, column: TableColumnModel }) {
    //Populate dependent dropdown options (Plant and Shop) for Shop Admin configuration mode
    if (this.currentAdminPage === AdminRole.SHOP_ADMIN) {
      if (eventDetails.column.name === 'company_id') {
        eventDetails.entry['plant_id'] = eventDetails.entry['shop_id'] = null;
        //update plant dropdown options based on selected company
        this.plantColumn.inputSelectOptions = Object.values(this.eventService.userPlantMap)
          .filter(plant => plant.company_id === eventDetails.event.value);
        //clear shop options since plant is reset
        this.shopColumn.inputSelectOptions = [];
      }
      else if (eventDetails.column.name === 'plant_id') {
        eventDetails.entry['shop_id'] = null;
        //update shop dropdown options based on selected plant
        this.shopColumn.inputSelectOptions = Object.values(this.eventService.userShopMap)
          .filter(shop => shop.plant_id === eventDetails.event.value);
      }
    }
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }
}